/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
import { BigNumber, ethers, utils } from "ethers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IERC20, IERC20__factory } from "../../codegen";
import { CollateralAssetResponse } from "../../codegen-api";
import {
  AllCollaterals,
  DepositWithdrawCollaterals,
  IsUSDC,
  getCollateralERC20Addresses,
} from "../../constants/addresses";
import { ChainIdEnum } from "../../enums/chain";
import {
  getRpcUrls,
  isNativeGasQuoteToken,
  isWethErc20Collateral,
} from "../../config/chains";
import { collateralAssetDecimals } from "../../utils/asset/assets";
import { splitSignature } from "../../utils/signing";
import { IWallet } from "../wallet/useWallet";
import { EIP2612_TYPE, validateEstimatedGas } from "./shared";

const getERC20 = (
  provider: any,
  asset: AllCollaterals,
  chainId?: ChainIdEnum
): IERC20 | undefined => {
  const signerOrProvider = provider?.getSigner() || provider;
  const l1 = getBalanceTokenAddress(asset, chainId);
  if (signerOrProvider && l1) {
    return IERC20__factory.connect(l1, signerOrProvider);
  }

  return undefined;
};

const getBalanceTokenAddress = (
  asset: AllCollaterals,
  chainId?: ChainIdEnum
): string | undefined => {
  const { l1 } = getCollateralERC20Addresses(asset, chainId);
  if (l1) {
    return l1;
  }

  // Some app-chain configs do not have a separate Native USDC token. In that
  // case wallet balances should still read the chain's regular USDC/MUSDC.
  if (IsUSDC(asset)) {
    return getCollateralERC20Addresses(
      DepositWithdrawCollaterals.Usdc,
      chainId
    ).l1;
  }

  return undefined;
};

const getReadProviders = (
  provider: IWallet["provider"],
  chainId?: ChainIdEnum
): ethers.providers.Provider[] => {
  const rpcProviders = Array.from(new Set(getRpcUrls(chainId).filter(Boolean)))
    .map((rpcUrl) => new ethers.providers.JsonRpcProvider(rpcUrl));

  return provider ? [...rpcProviders, provider] : rpcProviders;
};

const readWithFallback = async <T,>(
  providers: ethers.providers.Provider[],
  read: (provider: ethers.providers.Provider) => Promise<T>
): Promise<T> => {
  let lastError: unknown;

  for (const readProvider of providers) {
    try {
      return await read(readProvider);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("No provider available for contract read");
};

const canDepositWithPermit = (
  asset: DepositWithdrawCollaterals,
  chainId?: ChainIdEnum
) => {
  const validChainId =
    chainId === ChainIdEnum.ETH_MAINNET ||
    chainId === ChainIdEnum.SEPOLIA_TESTNET;
  // Note: BASECAMP_TESTNET removed because the USDC contract doesn't support EIP-2612 permit
  return asset === CollateralAssetResponse.Usdc && validChainId;
};

const getDomainInfoForAssetWithPermit = (
  asset: DepositWithdrawCollaterals,
  chainId?: ChainIdEnum,
  verifyingContractAddress?: string
) => {
  if (!verifyingContractAddress || !chainId) {
    return undefined;
  }

  switch (asset) {
    case DepositWithdrawCollaterals.Usdc:
      return {
        name: "USD Coin",
        version: chainId === ChainIdEnum.ETH_MAINNET ? "2" : "1",
        verifyingContract: verifyingContractAddress, // l1Contract.address,
        chainId,
      };
    case DepositWithdrawCollaterals.NativeUsdc:
      return {
        name: "USD Coin",
        version: chainId === ChainIdEnum.ETH_MAINNET ? "2" : "1",
        verifyingContract: verifyingContractAddress, // l1Contract.address,
        chainId,
      };
    default:
      return undefined;
  }
};

const useDepositERC20OrETH = (wallet: IWallet, asset: AllCollaterals) => {
  const { account, provider, chainId } = wallet;

  const [l1Contract, setContract] = useState<IERC20>();

  const [ethBalance, setEthBalance] = useState(BigNumber.from(0));
  const [erc20Balance, setErc20Balance] = useState(BigNumber.from(0));

  // On chains where native gas = USDC (e.g., Arc), show native + wrapped balance for USDC
  const nativeIsQuote = useMemo(() => isNativeGasQuoteToken(chainId), [chainId]);
  const wethIsErc20 = useMemo(() => isWethErc20Collateral(chainId), [chainId]);

  const balance = useMemo(() => {
    if (asset === DepositWithdrawCollaterals.Weth) {
      // When the native gas token is NOT ETH/WETH (e.g. Arc testnet where native = USDC),
      // WETH is a regular ERC20 — use the fetched ERC20 balance.
      // On chains where WETH IS the native token (e.g. Monad where WETH = MON),
      // use the native ethBalance instead.
      // WETH is a plain ERC-20 when native gas is not ETH-equivalent: Arc
      // (native = USDC) and DogeOS (native = DOGE, MWETH standalone). Only on
      // ETH/Monad chains (WETH wraps the native gas token) read the native balance.
      if (nativeIsQuote || wethIsErc20) {
        return erc20Balance;
      }
      return ethBalance;
    }
    // MON is the native gas token exposed as a collateral (its address is WNATIVE):
    // deposits wrap native MON, so show the native balance, not the wrapped-ERC20 one.
    if (asset === DepositWithdrawCollaterals.Mon) {
      return ethBalance;
    }
    if (nativeIsQuote && IsUSDC(asset)) {
      return ethBalance.add(erc20Balance);
    }
    return erc20Balance;
  }, [asset, erc20Balance, ethBalance, nativeIsQuote, wethIsErc20]);

  const decimals = useMemo(() => collateralAssetDecimals[asset], [asset]);

  const l2Address = useMemo(() => {
    const { l2 } = getCollateralERC20Addresses(asset, chainId);
    return l2;
  }, [asset, chainId]);

  useEffect(() => {
    setContract(getERC20(provider, asset, chainId));
  }, [asset, chainId, provider]);

  useEffect(() => {
    if (!account) {
      setEthBalance(BigNumber.from(0));
      setErc20Balance(BigNumber.from(0));
      return undefined;
    }

    let cancelled = false;
    const readProviders = getReadProviders(provider, chainId);

    readWithFallback(readProviders, (readProvider) =>
      readProvider.getBalance(account)
    )
      .then((bal) => {
        if (!cancelled) {
          setEthBalance(bal);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEthBalance(BigNumber.from(0));
        }
      });

    setErc20Balance(BigNumber.from(0));

    // Derive token address directly from current asset to avoid using a stale
    // l1Contract (which still points to the previously selected token when the
    // asset changes, causing the wrong balance to be fetched and displayed).
    const l1 = getBalanceTokenAddress(asset, chainId);
    if (l1 && utils.isAddress(l1)) {
      readWithFallback(readProviders, (readProvider) =>
        IERC20__factory.connect(l1, readProvider).balanceOf(account)
      )
        .then((bal) => {
          if (!cancelled) {
            setErc20Balance(bal);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setErc20Balance(BigNumber.from(0));
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [account, asset, provider, chainId]);

  const getAssetPermitSignature = useCallback(
    async (
      spender: string,
      // Amount of USDC
      amount: string,
      deadline: number
    ) => {
      const domain = getDomainInfoForAssetWithPermit(
        asset as DepositWithdrawCollaterals,
        chainId,
        l1Contract?.address
      );

      if (!account || !l1Contract || !provider || !domain) {
        return undefined;
      }

      const approveMessage = {
        owner: account,
        spender,
        value: ethers.utils.parseUnits(amount, decimals).toString(),
        nonce: (await l1Contract.nonces(account)).toNumber(),
        deadline,
      };

      // eslint-disable-next-line no-underscore-dangle
      const approveAssetSignature = await provider
        .getSigner()
        ._signTypedData(domain, { Permit: EIP2612_TYPE }, approveMessage);

      if (approveAssetSignature) {
        //@ts-ignore
        const splitted = splitSignature(approveAssetSignature);
        return splitted;
      }
      return undefined;
    },
    [account, asset, chainId, decimals, l1Contract, provider]
  );

  const approveAsset = useCallback(
    async (
      spender: string,
      // Amount of USDC
      amountUSDC: string
    ) => {
      if (
        !account ||
        !l1Contract ||
        !provider ||
        // Modern SDK wallets (Privy/Para/DogeOS) expose `request`, not the legacy
        // `sendAsync`; accept either.
        (!provider.provider.sendAsync && !provider.provider.request)
      ) {
        return undefined;
      }

      const amountBN = utils.parseUnits(amountUSDC, decimals);
      const estGas = await validateEstimatedGas(
        l1Contract.estimateGas.approve(spender, amountBN),
        BigNumber.from("100000")
      );
      const tx = await l1Contract.approve(spender, amountBN, {
        gasLimit: estGas,
      });
      return tx.wait(1);
    },
    [account, decimals, l1Contract, provider]
  );

  const getAllowance = useCallback(
    async (spender: string) => {
      if (account) {
        if (asset === CollateralAssetResponse.Weth) {
          return String(Infinity);
        }

        const l1 = getBalanceTokenAddress(asset, chainId);
        if (!l1 || !utils.isAddress(l1)) {
          return "0";
        }

        try {
          const allowance = await readWithFallback(
            getReadProviders(provider, chainId),
            (readProvider) =>
              IERC20__factory.connect(l1, readProvider).allowance(
                account,
                spender
              )
          );
          return utils.formatUnits(allowance, decimals);
        } catch {
          return "0";
        }
      }
      return "0";
    },
    [account, asset, chainId, decimals, provider]
  );

  return {
    l1Contract,
    l2Address,
    decimals,
    ethBalance,
    userBalance: balance,
    getAssetPermitSignature:
      asset !== CollateralAssetResponse.Weth &&
      canDepositWithPermit(asset as DepositWithdrawCollaterals, chainId)
        ? getAssetPermitSignature
        : undefined,
    approveAsset,
    getAllowance,
  };
};

export default useDepositERC20OrETH;
